import React, { useEffect, useMemo, useState } from 'react';
import { type TimeSlotAvailability } from '../../../models/booking/TimeSlotAvailability';
import { useBookingContext } from '../BookingContext';
import { addHoursToTime, formatLocalDate } from '../../../utils/time';
import { formatUsTime } from '../../../utils/bookingUtils';

const weekdayLabels = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const today = new Date();

interface DateTimeSectionProps {
  slots: TimeSlotAvailability[];
  isLoadingSlots: boolean;
  slotsError: string;
}

const DateTimeSection: React.FC<DateTimeSectionProps> = ({ slots, isLoadingSlots, slotsError }) => {
  const { booking, updateBooking, durationHours } = useBookingContext();

  const [viewDate, setViewDate] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDayOfMonth = viewDate.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthLabel = viewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  useEffect(() => {
    if (!booking.date) {
      updateBooking({ date: formatLocalDate(today), startTime: '', endTime: '' });
    }
  }, [booking.date, updateBooking]);

  useEffect(() => {
    if (!booking.startTime || !durationHours) {
      if (booking.endTime) {
        updateBooking({ endTime: '' });
      }
      return;
    }

    const nextEnd = addHoursToTime(booking.startTime, durationHours);
    if (booking.endTime !== nextEnd) {
      updateBooking({ endTime: nextEnd });
    }
  }, [booking.endTime, booking.startTime, durationHours, updateBooking]);

  const dayCells = Array.from({ length: firstDayOfMonth + daysInMonth }, (_, index) => {
    if (index < firstDayOfMonth) return null;
    return index - firstDayOfMonth + 1;
  });

  const availableSlots = useMemo(() => {
    const fullMotionCount = booking.fullMotionSimulators;
    const esportsCount = booking.esportsSimulators;

    return slots.filter((slot) => {
      const fullMotionTotal = slot.fullMotion?.available ?? 0;
      const esportsTotal = slot.esports?.available ?? 0;
      return fullMotionTotal >= fullMotionCount && esportsTotal >= esportsCount
        && slot.startTime.split(':')[1] !== '30';
    });
  }, [booking.esportsSimulators, booking.fullMotionSimulators, slots]);

  useEffect(() => {
    // Don't clear selected time while we're still loading availability;
    // otherwise returning from checkout can wipe a valid selection before slots arrive.
    if (isLoadingSlots || slotsError) return;

    if (booking.startTime && !availableSlots.some((s) => s.startTime === booking.startTime)) {
      updateBooking({ startTime: '', endTime: '' });
    }
  }, [availableSlots, booking.startTime, isLoadingSlots, slotsError, updateBooking]);

  const goToPreviousMonth = () => setViewDate(new Date(year, month - 1, 1));
  const goToNextMonth = () => setViewDate(new Date(year, month + 1, 1));

  return (
    <div>
      <label className="settings-field-label">Date:</label>
      <div className="booking-calendar mt-1 mb-3">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <button type="button" disabled={year === today.getFullYear() && month === today.getMonth()} className="btn btn-outline-secondary btn-sm" onClick={goToPreviousMonth}>
            Previous month
          </button>
          <strong>{monthLabel}</strong>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={goToNextMonth}>
            Next month
          </button>
        </div>
        <div className="booking-calendar-grid booking-calendar-weekdays">
          {weekdayLabels.map((label) => (
            <div key={label} className="booking-calendar-cell booking-calendar-weekday">
              {label}
            </div>
          ))}
        </div>
        <div className="booking-calendar-grid">
          {dayCells.map((day, index) => {
            if (!day) {
              return <div key={`empty-${index}`} className="booking-calendar-cell booking-calendar-empty" />;
            }
            const date = new Date(year, month, day);
            const isoDate = formatLocalDate(date);
            const isSelected = booking.date === isoDate;
            const isPastDayInCurrentMonth = year === today.getFullYear() && month === today.getMonth() && day < today.getDate();
            return (
              <button
                key={isoDate}
                type="button"
                className={`booking-calendar-cell booking-calendar-day ${isSelected ? 'btn-primary' : 'btn-outline-secondary'}`}
                disabled={isPastDayInCurrentMonth}
                onClick={() => updateBooking({ date: isoDate, startTime: '', endTime: '' })}
              >
                {day}
              </button>
            );
          })}
        </div>
      </div>

      <label className="settings-field-label">Time:</label>
      {isLoadingSlots ? (
        <p className="text-paragraphs mt-1">Loading available time slots...</p>
      ) : !booking.isSimulatorSelected ? (
        <p className="text-paragraphs mt-1">Please choose a simulator type and quantity first.</p>
      ) : availableSlots.length === 0 ? (
        <div className="alert alert-warning mt-1" role="alert">
          No available slots for selected simulator type and quantity.
        </div>
      ) : (
        <div className="booking-time-slots mt-1">
          {availableSlots.map((slot) => (
            <button
              key={slot.startTime}
              type="button"
              className={`btn ${booking.startTime === slot.startTime ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() =>
                updateBooking({
                  startTime: slot.startTime,
                  endTime: durationHours ? addHoursToTime(slot.startTime, durationHours) : ''
                })
              }
            >
              <div>{formatUsTime(slot.startTime)}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default DateTimeSection;
